import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  FileUp,
  Plus,
  Trash2,
  Edit,
  Eye,
  Loader2,
  Users,
  MapPin,
  GraduationCap,
  FileText,
  Download,
  CheckCircle,
  Clock,
  Target,
  Send,
  Save,
} from 'lucide-react';

const DocumentCampaigns = () => {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [targetingOptions, setTargetingOptions] = useState({
    cities: [],
    programs: [],
    classes: []
  });
  
  // Create campaign dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    title: '',
    description: '',
    deadline: '',
    document_types: [],
    targeting: {
      all_parents: true,
      cities: [],
      program_ids: [],
      class_ids: []
    }
  });
  
  // View campaign dialog
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignSubmissions, setCampaignSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  
  // Edit campaign dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchTargetingOptions();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/document-campaigns/campaigns');
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchTargetingOptions = async () => {
    try {
      const response = await api.get('/document-campaigns/targeting-options');
      setTargetingOptions(response.data);
    } catch (error) {
      console.error('Failed to load targeting options:', error);
    }
  };

  const fetchCampaignDetails = async (campaignId) => {
    setLoadingSubmissions(true);
    try {
      const response = await api.get(`/document-campaigns/campaigns/${campaignId}`);
      setSelectedCampaign(response.data);
      setCampaignSubmissions(response.data.submissions || []);
    } catch (error) {
      toast.error('Failed to load campaign details');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const addDocumentType = () => {
    const newType = {
      id: `doc_${Date.now()}`,
      label: '',
      description: '',
      required: true
    };
    setCampaignForm(prev => ({
      ...prev,
      document_types: [...prev.document_types, newType]
    }));
  };

  const updateDocumentType = (index, field, value) => {
    setCampaignForm(prev => ({
      ...prev,
      document_types: prev.document_types.map((dt, i) =>
        i === index ? { ...dt, [field]: value } : dt
      )
    }));
  };

  const removeDocumentType = (index) => {
    setCampaignForm(prev => ({
      ...prev,
      document_types: prev.document_types.filter((_, i) => i !== index)
    }));
  };

  const createCampaign = async () => {
    if (!campaignForm.title.trim()) {
      toast.error('Please enter a campaign title');
      return;
    }
    if (campaignForm.document_types.length === 0) {
      toast.error('Please add at least one document type');
      return;
    }
    if (campaignForm.document_types.some(dt => !dt.label.trim())) {
      toast.error('Please fill in all document type names');
      return;
    }

    setSaving(true);
    try {
      await api.post('/document-campaigns/campaigns', campaignForm);
      toast.success('Document campaign created successfully!');
      setCreateDialog(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setCampaignForm({
      title: '',
      description: '',
      deadline: '',
      document_types: [],
      targeting: {
        all_parents: true,
        cities: [],
        program_ids: [],
        class_ids: []
      }
    });
  };

  const toggleCampaignActive = async (campaign) => {
    try {
      await api.put(`/document-campaigns/campaigns/${campaign.id}`, {
        is_active: !campaign.is_active
      });
      toast.success(campaign.is_active ? 'Campaign deactivated' : 'Campaign activated');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      title: campaign.title || '',
      description: campaign.description || '',
      deadline: campaign.deadline ? campaign.deadline.split('T')[0] : '',
      document_types: campaign.document_types || [],
      targeting: campaign.targeting || {
        all_parents: true,
        cities: [],
        program_ids: [],
        class_ids: []
      }
    });
    setEditDialog(true);
  };

  const updateEditDocumentType = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      document_types: prev.document_types.map((dt, i) =>
        i === index ? { ...dt, [field]: value } : dt
      )
    }));
  };

  const addEditDocumentType = () => {
    const newType = {
      id: `doc_${Date.now()}`,
      label: '',
      description: '',
      required: true
    };
    setEditForm(prev => ({
      ...prev,
      document_types: [...prev.document_types, newType]
    }));
  };

  const removeEditDocumentType = (index) => {
    setEditForm(prev => ({
      ...prev,
      document_types: prev.document_types.filter((_, i) => i !== index)
    }));
  };

  const saveEditCampaign = async () => {
    if (!editForm.title.trim()) {
      toast.error('Please enter a campaign title');
      return;
    }
    if (editForm.document_types.length === 0) {
      toast.error('Please add at least one document type');
      return;
    }
    if (editForm.document_types.some(dt => !dt.label.trim())) {
      toast.error('Please fill in all document type names');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/document-campaigns/campaigns/${editingCampaign.id}`, editForm);
      toast.success('Campaign updated successfully!');
      setEditDialog(false);
      setEditingCampaign(null);
      setEditForm(null);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async (campaignId) => {
    try {
      const response = await api.get(`/document-campaigns/campaigns/${campaignId}/export-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'submissions.html');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export ready - open in browser and print to PDF');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const viewFile = async (fileId) => {
    try {
      const response = await api.get(`/document-campaigns/files/${fileId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to open file');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Document Campaigns">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Document Campaigns">
      <div className="space-y-6" data-testid="document-campaigns-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Document Campaigns</h2>
            <p className="text-slate-500">Request documents from parents with targeted campaigns</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => setCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-campaign-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Campaign
          </Button>
          </div>
        </div>

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileUp className="h-12 w-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Campaigns Yet</h3>
              <p className="text-slate-500 mt-2">Create your first document campaign to request files from parents.</p>
              <Button
                onClick={() => setCreateDialog(true)}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {campaigns.map(campaign => (
              <Card key={campaign.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 lg:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{campaign.title}</h3>
                        {campaign.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                        )}
                      </div>
                      
                      {campaign.description && (
                        <p className="text-slate-500 text-sm mb-3">{campaign.description}</p>
                      )}
                      
                      {/* Document Types */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {campaign.document_types?.map((dt, idx) => (
                          <Badge key={idx} variant="outline" className="bg-blue-50">
                            <FileText className="h-3 w-3 mr-1" />
                            {dt.label}
                            {dt.required && <span className="text-red-500 ml-1">*</span>}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Targeting Info */}
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        {campaign.targeting?.all_parents ? (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            All Parents
                          </span>
                        ) : (
                          <>
                            {campaign.targeting?.cities?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {campaign.targeting.cities.join(', ')}
                              </span>
                            )}
                            {campaign.targeting?.class_ids?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <GraduationCap className="h-3 w-3" />
                                {campaign.targeting.class_ids.length} classes
                              </span>
                            )}
                          </>
                        )}
                        {campaign.deadline && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Due: {new Date(campaign.deadline).toLocaleDateString()}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {campaign.submission_count || 0} submissions
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          fetchCampaignDetails(campaign.id);
                          setViewDialog(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(campaign)}
                        data-testid={`edit-campaign-${campaign.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => exportPdf(campaign.id)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Export
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleCampaignActive(campaign)}
                      >
                        {campaign.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Campaign Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileUp className="h-5 w-5 text-blue-600" />
                Create Document Campaign
              </DialogTitle>
              <DialogDescription>
                Request specific documents from targeted parents.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Campaign Title *</Label>
                  <Input
                    value={campaignForm.title}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Address Verification 2026"
                    data-testid="campaign-title-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={campaignForm.description}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Explain what documents are needed and why..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Deadline (Optional)</Label>
                  <Input
                    type="date"
                    value={campaignForm.deadline}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, deadline: e.target.value }))}
                  />
                </div>
              </div>

              {/* Document Types */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Documents to Request *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addDocumentType}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Document
                  </Button>
                </div>

                {campaignForm.document_types.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed">
                    <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No documents added yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaignForm.document_types.map((dt, index) => (
                      <div key={dt.id} className="p-4 border rounded-lg bg-slate-50">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-medium text-slate-600">Document {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDocumentType(index)}
                            className="text-red-500 h-6 px-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs">Document Name *</Label>
                            <Input
                              value={dt.label}
                              onChange={(e) => updateDocumentType(index, 'label', e.target.value)}
                              placeholder="e.g., Address Proof, School ID Card"
                              className="h-9"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Description (Optional)</Label>
                            <Input
                              value={dt.description || ''}
                              onChange={(e) => updateDocumentType(index, 'description', e.target.value)}
                              placeholder="e.g., Utility bill or bank statement"
                              className="h-9"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <Checkbox
                                checked={dt.required}
                                onCheckedChange={(checked) => updateDocumentType(index, 'required', checked)}
                              />
                              <span className="text-sm">Required document</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Targeting */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-3 block">
                  <Target className="h-4 w-4 inline mr-2" />
                  Target Audience
                </Label>

                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.targeting.all_parents}
                      onCheckedChange={(checked) => setCampaignForm(prev => ({
                        ...prev,
                        targeting: { ...prev.targeting, all_parents: checked }
                      }))}
                    />
                    <span>Send to all parents</span>
                  </label>

                  {!campaignForm.targeting.all_parents && (
                    <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                      {/* City Filter */}
                      <div>
                        <Label className="text-sm">Filter by City</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {targetingOptions.cities.map(city => (
                            <label key={city} className="flex items-center gap-1 cursor-pointer">
                              <Checkbox
                                checked={campaignForm.targeting.cities.includes(city)}
                                onCheckedChange={(checked) => {
                                  setCampaignForm(prev => ({
                                    ...prev,
                                    targeting: {
                                      ...prev.targeting,
                                      cities: checked
                                        ? [...prev.targeting.cities, city]
                                        : prev.targeting.cities.filter(c => c !== city)
                                    }
                                  }));
                                }}
                              />
                              <span className="text-sm">{city}</span>
                            </label>
                          ))}
                        </div>
                        {targetingOptions.cities.length === 0 && (
                          <p className="text-xs text-slate-400 mt-1">No cities found in parent profiles</p>
                        )}
                      </div>

                      {/* Class Filter */}
                      <div>
                        <Label className="text-sm">Filter by Class/Program</Label>
                        <div className="max-h-40 overflow-y-auto border rounded p-2 mt-2">
                          {targetingOptions.classes.map(cls => (
                            <label key={cls.id} className="flex items-center gap-2 cursor-pointer py-1">
                              <Checkbox
                                checked={campaignForm.targeting.class_ids.includes(cls.id)}
                                onCheckedChange={(checked) => {
                                  setCampaignForm(prev => ({
                                    ...prev,
                                    targeting: {
                                      ...prev.targeting,
                                      class_ids: checked
                                        ? [...prev.targeting.class_ids, cls.id]
                                        : prev.targeting.class_ids.filter(c => c !== cls.id)
                                    }
                                  }));
                                }}
                              />
                              <span className="text-sm">{cls.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button
                onClick={createCampaign}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Create Campaign
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Campaign Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedCampaign?.title || 'Campaign Details'}
              </DialogTitle>
              <DialogDescription>
                View submissions and download documents.
              </DialogDescription>
            </DialogHeader>

            {loadingSubmissions ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : selectedCampaign && (
              <div className="space-y-4">
                {/* Campaign Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">{selectedCampaign.description || 'No description'}</p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>Documents requested: {selectedCampaign.document_types?.length || 0}</span>
                    <span>Submissions: {campaignSubmissions.length}</span>
                    {selectedCampaign.deadline && (
                      <span>Deadline: {new Date(selectedCampaign.deadline).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>

                {/* Submissions Table */}
                {campaignSubmissions.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No submissions yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Parent</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Documents</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campaignSubmissions.map(sub => (
                        <TableRow key={sub.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{sub.parent_name}</p>
                              <p className="text-xs text-slate-500">{sub.parent_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(sub.submitted_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              sub.status === 'submitted' ? 'bg-green-100 text-green-700' :
                              sub.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }>
                              {sub.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {sub.documents?.map((doc, idx) => {
                                const docType = selectedCampaign.document_types?.find(
                                  dt => dt.id === doc.document_type_id
                                );
                                return (
                                  <Badge
                                    key={idx}
                                    variant="outline"
                                    className="cursor-pointer hover:bg-blue-50"
                                    onClick={() => viewFile(doc.id)}
                                  >
                                    <FileText className="h-3 w-3 mr-1" />
                                    {docType?.label || doc.document_type_id}
                                  </Badge>
                                );
                              })}
                              {(!sub.documents || sub.documents.length === 0) && (
                                <span className="text-xs text-slate-400">No files</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                sub.documents?.forEach(doc => viewFile(doc.id));
                              }}
                              disabled={!sub.documents?.length}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => selectedCampaign && exportPdf(selectedCampaign.id)}
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              <Button onClick={() => setViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Campaign Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Document Campaign
              </DialogTitle>
              <DialogDescription>
                Update campaign details or extend the deadline.
              </DialogDescription>
            </DialogHeader>

            {editForm && (
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <Label>Campaign Title *</Label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Address Verification 2026"
                    />
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what documents are needed and why..."
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label>Deadline</Label>
                    <Input
                      type="date"
                      value={editForm.deadline}
                      onChange={(e) => setEditForm(prev => ({ ...prev, deadline: e.target.value }))}
                    />
                    <p className="text-xs text-slate-500 mt-1">Extend the deadline if needed</p>
                  </div>
                </div>

                {/* Document Types */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Required Documents</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addEditDocumentType}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Document Type
                    </Button>
                  </div>
                  
                  {editForm.document_types.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed rounded-lg">
                      <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">No document types added</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {editForm.document_types.map((docType, index) => (
                        <div key={docType.id} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1 space-y-2">
                            <Input
                              value={docType.label}
                              onChange={(e) => updateEditDocumentType(index, 'label', e.target.value)}
                              placeholder="Document name (e.g., Proof of Address)"
                            />
                            <Input
                              value={docType.description || ''}
                              onChange={(e) => updateEditDocumentType(index, 'description', e.target.value)}
                              placeholder="Description or instructions (optional)"
                              className="text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={docType.required}
                                onChange={(e) => updateEditDocumentType(index, 'required', e.target.checked)}
                                className="rounded"
                              />
                              Required
                            </label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeEditDocumentType(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Targeting */}
                <div>
                  <Label className="mb-3 block">Target Audience</Label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editForm.targeting?.all_parents}
                        onChange={(e) => setEditForm(prev => ({
                          ...prev,
                          targeting: { ...prev.targeting, all_parents: e.target.checked }
                        }))}
                        className="rounded"
                      />
                      <span>All Parents</span>
                    </label>
                    
                    {!editForm.targeting?.all_parents && (
                      <div className="pl-6 space-y-3">
                        {/* City selection */}
                        <div>
                          <Label className="text-sm">Filter by Cities</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {targetingOptions.cities?.map(city => (
                              <label key={city} className="flex items-center gap-1 text-sm">
                                <input
                                  type="checkbox"
                                  checked={editForm.targeting?.cities?.includes(city)}
                                  onChange={(e) => {
                                    const cities = editForm.targeting?.cities || [];
                                    setEditForm(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        cities: e.target.checked
                                          ? [...cities, city]
                                          : cities.filter(c => c !== city)
                                      }
                                    }));
                                  }}
                                  className="rounded"
                                />
                                {city}
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {/* Class selection */}
                        <div>
                          <Label className="text-sm">Filter by Classes</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {targetingOptions.classes?.map(cls => (
                              <label key={cls.id} className="flex items-center gap-1 text-sm">
                                <input
                                  type="checkbox"
                                  checked={editForm.targeting?.class_ids?.includes(cls.id)}
                                  onChange={(e) => {
                                    const classIds = editForm.targeting?.class_ids || [];
                                    setEditForm(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        class_ids: e.target.checked
                                          ? [...classIds, cls.id]
                                          : classIds.filter(id => id !== cls.id)
                                      }
                                    }));
                                  }}
                                  className="rounded"
                                />
                                {cls.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveEditCampaign} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DocumentCampaigns;
